import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Modal from "@/components/modal";
import api from "@/services/api";

export default function EditResourceModal({ resource, onClose, onUpdated, isOpen = true }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    type: "article",
    url: "",
    image: "",
    author: "",
    description: "",
    tags: [],
  });
  const [tagInput, setTagInput] = useState("");

  const commonTags = ["Tech", "Product", "Leadership", "Admin"];

  useEffect(() => {
    if (resource) {
      setForm({
        title: resource.title || resource.name || "",
        type: resource.type || "article",
        url: resource.url || "",
        image: resource.image || "",
        author: resource.author || "",
        description: resource.description || "",
        tags: resource.tags || [],
      });
    }
  }, [resource]);

  const addTag = (tag) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !form.tags.includes(trimmedTag)) {
      setForm({ ...form, tags: [...form.tags, trimmedTag] });
    }
    setTagInput("");
  };

  const removeTag = (tagToRemove) => {
    setForm({ ...form, tags: form.tags.filter((tag) => tag !== tagToRemove) });
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag(tagInput);
    }
  };

  // Extract YouTube ID and set thumbnail
  useEffect(() => {
    if (!form.url) return;
    
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = form.url.match(youtubeRegex);

    if (match && match[1]) {
      const videoId = match[1];
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      
      setForm(prev => {
        const updates = { type: "video" };
        
        if (!prev.image || prev.image.includes("img.youtube.com") || prev.image.includes("youtube.com")) {
          updates.image = thumbnailUrl;
        }
        
        return { ...prev, ...updates };
      });
    }
  }, [form.url]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Convert form data to workshop format (name instead of title)
      const workshopData = {
        ...form,
        name: form.title, // Workshop uses 'name' instead of 'title'
      };
      delete workshopData.title; // Remove title field

      let res;
      if (resource) {
        res = await api.put(`/workshop/${resource._id || resource.id}`, workshopData);
      } else {
        res = await api.post("/workshop", workshopData);
      }
      
      if (res.ok) {
        toast.success(resource ? "Resource updated!" : "Resource added!");
        onUpdated();
      } else {
        toast.error("Operation failed");
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !resource) return null;

  return (
    <Modal isOpen={true} onClose={onClose} className="max-w-md w-full p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">{resource ? "Edit Resource" : "Add New Resource"}</h2>
      <form onSubmit={handleSubmit} className="space-y-4" onClick={(e) => e.stopPropagation()}>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500">
            <option value="article">Article / Link</option>
            <option value="book">Book</option>
            <option value="video">Video</option>
            <option value="newsletter">Newsletter</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input
            required
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g. Thinking in Systems"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Author (Optional)</label>
          <input
            type="text"
            value={form.author}
            onChange={(e) => setForm({ ...form, author: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="e.g. Donella Meadows"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">URL (Resource Link)</label>
          <input
            type="url"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://..."
          />
          <p className="text-xs text-gray-500 mt-1">Paste a YouTube link to auto-fetch the thumbnail.</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (Cover/Thumbnail)</label>
          <input
            type="url"
            value={form.image}
            onChange={(e) => setForm({ ...form, image: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="https://example.com/image.jpg"
          />
          {form.image && (
            <div className="mt-2">
              <img 
                src={form.image} 
                alt="Preview" 
                className="w-full h-32 object-cover rounded-lg border border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500"
            placeholder="Why is this resource valuable?"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-sm">
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="text-blue-700 hover:text-blue-900">
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                className="flex-1 rounded-lg border-gray-300 focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Type tag and press Enter"
              />
              <button
                type="button"
                onClick={() => addTag(tagInput)}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-1">
              {commonTags
                .filter((tag) => !form.tags.includes(tag))
                .map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => addTag(tag)}
                    className="px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
                    + {tag}
                  </button>
                ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50">
            {loading ? (resource ? "Saving..." : "Adding...") : (resource ? "Save Changes" : "Add Resource")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

